import React, { PureComponent, ReactNode } from 'react';
import { MonitoredEvent } from 'app/model/SOL49';
import memoizeOne from 'memoize-one';
import { ManagedColumn, SINGLE_SELECTION, SingleSelection } from 'app/common/table/ManagedTable';
import Utils from 'app/common/util/Utils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { notification, Spin } from 'antd';
import { Flex } from 'app/components/common/Flex';
import { SidePanelSeparator } from 'app/components/common/SidePanelSeparator';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { PartialDashboardDataUpdateEvent } from 'app/common/eventbus/Events';
import { EntityType } from 'app/model/client/SOL49';
import { MonitoredEventsSettings } from 'app/components/dashboard/global/events/MonitoredEventsSettings';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import AppContext from 'app/common/context/AppContext';
import { MonitoredEventsFilters } from 'app/components/dashboard/global/events/MonitoredEventFilters';
import './MonitoredEventTable.less';

export interface AbstractMonitoredEventPanelProps<T extends MonitoredEvent> {
    events: T[];
    defaultEvents?: string[];
    selected?: string;
    settings: MonitoredEventsSettings;
    onSettingsChange?: (settings: MonitoredEventsSettings) => void;
    filters?: MonitoredEventsFilters;
    onFiltersChange?: (filters: MonitoredEventsFilters) => void;
    onWidthChange?: (widthEm: string) => void;
}

export interface AbstractMonitoredEventPanelState<T extends MonitoredEvent> {
    events: T[];
    selected: string;
    loading: boolean;
}

export abstract class AbstractMonitoredEventPanel<
    T extends MonitoredEvent,
    P extends AbstractMonitoredEventPanelProps<T> = AbstractMonitoredEventPanelProps<T>,
    S extends AbstractMonitoredEventPanelState<T> = AbstractMonitoredEventPanelState<T>,
> extends PureComponent<P, S> {
    protected static TABLE_MIN_HEIGHT_EM = '55em';
    protected abstract columns: ManagedColumn<T>[];
    protected selection = memoizeOne(
        (selected: T): SingleSelection<T> => ({
            selectionMode: SINGLE_SELECTION,
            onSelectionChange: selection => this.setState({ selected: selection?.id }),
            selectedItem: selected,
        }),
    );

    protected constructor(props) {
        super(props);
        this.state = {
            events: this.props.events,
            selected: this.props.selected,
            loading: false,
        } as unknown as S;
    }

    componentDidUpdate(prevProps: Readonly<AbstractMonitoredEventPanelProps<T>>) {
        if (Utils.isNotShallowEquals(prevProps.events, this.props.events)) {
            this.setState({
                events: this.props.events,
                selected:
                    this.state.selected &&
                    (this.props.events.find(e => e.id === this.state.selected)?.id || this.props.events[0]?.id),
            });
        }

        if (prevProps.selected !== this.props.selected) {
            this.setState({
                selected: this.props.selected,
            });
        }
    }

    onSettingsChange = (settings: MonitoredEventsSettings) => this.props.onSettingsChange(settings);
    onFiltersChange = (filters: MonitoredEventsFilters) => this.props.onFiltersChange(filters);

    protected highlight = (event: MonitoredEvent): boolean => this.props.defaultEvents?.includes(event.id);

    render() {
        let events = this.filterEvents(this.state.events);
        events = this.sortEvents(events);
        const selected = events.find(note => note.id === this.state.selected);
        const defaultEvent = selected || this.getDefaultEvent();

        return (
            <Spin spinning={this.state.loading}>
                <Flex style={{ minHeight: '75em' }}>
                    <Flex direction="vertical" style={{ padding: '0.5em' }} grow={false} shrink={false} size={'60em'}>
                        {this.getToolbar()}
                        <Flex
                            style={{ minHeight: AbstractMonitoredEventPanel.TABLE_MIN_HEIGHT_EM }}
                            className="monitored-event-table">
                            {this.getTable(events, selected)}
                        </Flex>
                    </Flex>
                    <SidePanelSeparator hideSeparator={true} />
                    <Flex className="monitored-event-form">{this.getForm(defaultEvent)}</Flex>
                </Flex>
            </Spin>
        );
    }

    protected abstract getToolbar(): ReactNode;

    protected abstract refresh(): void;

    protected abstract getDefaultEvent(): T;

    protected abstract getForm(event: T): ReactNode;

    protected abstract getTable(events: T[], selected: T): ReactNode;

    protected filterEvents(events: T[]): T[] {
        // No filter applied, child classes can overwrite this if needed
        return this.props.settings?.activeOnly
            ? events.filter(e => TimePeriodUtils.intersects(e.validity, AppContext.getTodayPeriod()))
            : events;
    }

    protected sortEvents(events: T[]): T[] {
        return events.sort(MonitoredEventUtils.cmpMonitoredEvent);
    }

    protected onCreate = (event: T) => {
        this.setState({ loading: true }); // reset after refresh()
        ServiceFactory.sol49Service()
            .addMonitoredEvent(event)
            .then(reply => {
                PartialDashboardDataUpdateEvent.fireEvent(EntityType.MONITORED_EVENT);
                this.setState({ selected: reply.data }, () => this.refresh());
            })
            .catch(err => notification.error({ message: 'Error creating event', description: '' + err }));
    };

    protected onUpdate = (event: T) => {
        this.setState({ loading: true }); // reset after refresh()
        ServiceFactory.sol49Service()
            .updateMonitoredEvent(event)
            .then(reply => {
                if (reply) {
                    PartialDashboardDataUpdateEvent.fireEvent(EntityType.MONITORED_EVENT);
                    this.refresh();
                } else {
                    notification.error({ message: 'Error updating note' });
                }
            })
            .catch(err => notification.error({ message: 'Error updating event', description: '' + err }));
    };

    protected onDelete = () => {
        if (!this.state.selected) {
            console.error('Error: trying to delete a note without a selection !');
            return;
        }

        this.setState({ loading: true });
        ServiceFactory.sol49Service()
            .deleteMonitoredEvent(this.state.selected)
            .then(reply => {
                if (reply) {
                    PartialDashboardDataUpdateEvent.fireEvent(EntityType.MONITORED_EVENT);
                    this.setState({ selected: undefined }, () => this.refresh());
                } else {
                    notification.error({ message: 'Error deleting event' });
                }
            })
            .catch(err => notification.error({ message: 'Error deleting event', description: '' + err }));
    };
}
